<script setup lang="ts">
import type {
  Event,
  EventPreference,
  EventsOverview,
  EventsOverviewProduct,
  Product,
} from "mws-ts-v2-sdk";

type Props = {
  event?: EventsOverview | Event | EventPreference;
  products?: Product[] | EventsOverviewProduct[];
  isLoading: boolean;
  hasPadding?: boolean;
  eventListIndex?: number;
  enableVirtualization?: boolean;
  trackingSource: string;
};

const {
  hasPadding = true,
  enableVirtualization,
  event,
  products,
} = defineProps<Props>();

type Emits = {
  (e: "product-finished"): void;
  (e: "product-clicked", product: Product | EventsOverviewProduct): void;
};

defineEmits<Emits>();

const productsSlider = useTemplateRef("productsSlider");
const { y, height } = useElementBounding(productsSlider, {
  immediate: false,
});

const debouncedY = refDebounced(y, 50);

const isCloseToViewport = computed(() => {
  if (!enableVirtualization) return true;

  // height = 0 means its not loaded yet
  return (
    height.value !== 0 && debouncedY.value > -4000 && debouncedY.value < 4000
  );
});
</script>

<template>
  <div
    ref="productsSlider"
    class="products-slider"
    :class="hasPadding && 'pt-6 pb-4'"
  >
    <!-- we only render the actual content of this when it's close to the viewport for performance -->
    <div class="products-slider__container">
      <slot name="heading" />

      <div
        class="products-slider__products"
        :class="hasPadding && 'pt-4'"
      >
        <ul
          v-if="isLoading || !isCloseToViewport"
          class="flex gap-x-1 overflow-hidden"
        >
          <ProductCardSkeleton
            v-for="productIndex in 5"
            :key="productIndex"
            as="li"
          />
        </ul>
        <template v-else>
          <ProductCardContainer
            data-test="product-card-container"
            :event="event"
            :products="products ?? []"
            :event-list-index="eventListIndex"
            :tracking-source="trackingSource"
            @product-finished="$emit('product-finished')"
            @product-clicked="(product) => $emit('product-clicked', product)"
          />
          <LazyBasePlaceholderNoResults
            v-if="products?.length === 0"
            variant="productsSlider"
            class="h-full justify-center"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.products-slider {
  @apply items-center;
  @apply mb-3 sm:mb-10 md:mb-0;

  &__container {
    @apply w-full h-full flex flex-col;
  }

  &__missing-currency {
    @apply py-4 px-2 col-span-12;
  }

  &__products {
    @apply col-span-12 h-full;
  }

  --header-size: 70px;
  --product-card-bottom-info-size: 170px;

  /* below height styles correspond to different product card sizes on different screen sizes */
  height: calc(
    66vw + var(--product-card-bottom-info-size) + var(--header-size) + 40px
  ); /* 1.5 cards */
  @media (min-width: 768px) {
    height: calc(
      33vw + var(--product-card-bottom-info-size) + var(--header-size)
    ); /* 3 cards on md */
  }
  @media (min-width: 1024px) {
    height: calc(
      25vw + var(--product-card-bottom-info-size) + var(--header-size)
    ); /* 4 cards on lg */
  }
  @media (min-width: 1600px) {
    height: calc(
      20vw + var(--product-card-bottom-info-size) + var(--header-size)
    ); /* 5 cards on 3xl */
  }
}
</style>
